/**
 * Browse GeoJSON Catalogue
 *
 * Primary discovery mechanism for MapQuizzr V2.
 * Users explore the GeoJSON catalogue, preview datasets, then create quizzes.
 *
 * Flow: Browse → Preview → Studio → Quiz
 */

import { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, FileJson, Sparkles } from "lucide-react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { geojsonDatasets, type GeoJsonDataset } from "@/data/geojsonDatasets";
import { BookMascot } from "@/components/ui/BookMascot";
import { getCountryFlag } from "@/utils/countryFlags";
import { CountryFilter } from "@/components/shared/CountryFilter";
import { SizeBadge } from "@/components/shared/SizeBadge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { calculateQuizSize, getAllSizes, type QuizSize } from "@/utils/quizSizing";
import { cn } from "@/lib/utils";

const ITEMS_PER_PAGE = 24;

// Category definitions
type Category = "all" | "phone" | "admin" | "cities" | "postal";

const CATEGORIES: { value: Category; label: string; icon: string }[] = [
  { value: "all", label: "All", icon: "🌍" },
  { value: "phone", label: "Phone Codes", icon: "📞" },
  { value: "admin", label: "Administrative", icon: "🗺️" },
  { value: "cities", label: "Cities", icon: "🏙️" },
  { value: "postal", label: "Postal Codes", icon: "📮" },
];

export function BrowseGeoJSONPage() {
  const navigate = useNavigate();
  const resultsRef = useRef<HTMLElement>(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<Category>("all");
  const [countryFilter, setCountryFilter] = useState<string | null>(null);
  const [sizeFilter, setSizeFilter] = useState<QuizSize | "all">("all");
  const [currentPage, setCurrentPage] = useState(1);

  // Extract unique countries
  const countries = useMemo(() => {
    const countrySet = new Set<string>();
    geojsonDatasets.forEach((dataset) => {
      countrySet.add(dataset.geography);
    });
    return Array.from(countrySet).sort();
  }, []);

  // Categorize a dataset based on its topics
  const getDatasetCategory = (dataset: GeoJsonDataset): Category => {
    const topics = dataset.topics.map(t => t.toLowerCase()).join(" ");
    if (topics.includes("phone") || topics.includes("area code")) return "phone";
    if (topics.includes("admin") || topics.includes("province") || topics.includes("state")) return "admin";
    if (topics.includes("cit") || topics.includes("urban")) return "cities";
    if (topics.includes("postal") || topics.includes("zip")) return "postal";
    return "all";
  };

  // Filter and search datasets
  const filteredDatasets = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return geojsonDatasets
      .filter((dataset) => {
        // Category filter
        if (categoryFilter !== "all") {
          const datasetCategory = getDatasetCategory(dataset);
          if (datasetCategory !== categoryFilter) {
            return false;
          }
        }

        // Country filter
        if (countryFilter && dataset.geography !== countryFilter) {
          return false;
        }

        // Size filter
        if (sizeFilter !== "all") {
          const datasetSize = calculateQuizSize(dataset.featureCount);
          if (datasetSize !== sizeFilter) {
            return false;
          }
        }

        // Search term
        if (term) {
          const searchable = [
            dataset.title,
            dataset.description,
            dataset.geography,
            ...dataset.topics,
          ].join(" ").toLowerCase();

          return searchable.includes(term);
        }

        return true;
      })
      .map((dataset) => {
        // Calculate relevance score for sorting
        if (!term) return { dataset, score: 0 };

        let score = 0;
        const titleLower = dataset.title.toLowerCase();
        const descLower = dataset.description.toLowerCase();
        const geographyLower = dataset.geography.toLowerCase();

        if (titleLower.includes(term)) score += 3;
        if (geographyLower.includes(term)) score += 2;
        if (descLower.includes(term)) score += 1;

        return { dataset, score };
      })
      .sort((a, b) => b.score - a.score)
      .map((item) => item.dataset);
  }, [searchTerm, categoryFilter, countryFilter, sizeFilter]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, categoryFilter, countryFilter, sizeFilter]);

  // Pagination
  const totalPages = Math.ceil(filteredDatasets.length / ITEMS_PER_PAGE);
  const paginatedDatasets = filteredDatasets.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Helper to scroll to results
  const scrollToResults = () => {
    if (resultsRef.current) {
      const elementPosition = resultsRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 100;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    }
  };

  // Scroll to results when country is selected on map
  const handleCountrySelected = () => {
    scrollToResults();
  };

  // Handle pagination with scroll
  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
    scrollToResults();
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm !== "" || categoryFilter !== "all" || countryFilter !== null || sizeFilter !== "all";

  // Reset all filters
  const resetFilters = () => {
    setSearchTerm("");
    setCategoryFilter("all");
    setCountryFilter(null);
    setSizeFilter("all");
  };

  return (
    <MainLayout
      title="Browse GeoJSON Datasets"
      subtitle={`${filteredDatasets.length} dataset${filteredDatasets.length === 1 ? "" : "s"} available`}
    >
      <div className="container mx-auto p-4 md:p-6 max-w-7xl space-y-6">
        {/* Filters Section */}
        <section className="rounded-3xl border border-border/70 bg-white/80 p-6 shadow-xl shadow-primary/10 backdrop-blur dark:bg-secondary/70">
          <div className="space-y-6">
            {/* Category Pills */}
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.24em] text-foreground/50 mb-3">
                Category
              </p>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    onClick={() => setCategoryFilter(cat.value)}
                    className={cn(
                      "inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all",
                      categoryFilter === cat.value
                        ? "bg-primary text-primary-foreground shadow-md"
                        : "bg-muted/50 text-foreground/70 hover:bg-muted hover:text-foreground"
                    )}
                  >
                    <span>{cat.icon}</span>
                    <span>{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Search and Size Filter Row */}
            <div className="grid gap-4 lg:grid-cols-2">
              {/* Search */}
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-foreground/50">
                  Search
                </p>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-foreground/40" />
                  <Input
                    type="text"
                    placeholder="Find by country, topic, or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 rounded-xl"
                    autoFocus
                  />
                </div>
              </div>

              {/* Size Filter */}
              <div className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-foreground/50">
                  Size
                </p>
                <Select value={sizeFilter} onValueChange={(value) => setSizeFilter(value as QuizSize | "all")}>
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Any Size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Any Size</SelectItem>
                    {getAllSizes().map((size) => (
                      <SelectItem key={size.value} value={size.value}>
                        {size.label} ({size.range})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Country Filter (Map-based) - Full width */}
            <CountryFilter
              availableCountries={countries}
              selectedCountry={countryFilter}
              onCountryChange={setCountryFilter}
              onCountrySelected={handleCountrySelected}
            />

            {/* Filter Summary - Compact single row */}
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
              <div className="inline-flex items-center gap-2 text-foreground/60">
                <FileJson className="h-4 w-4" />
                <span>
                  Showing <strong className="text-foreground">{filteredDatasets.length}</strong> of{" "}
                  <strong className="text-foreground">{geojsonDatasets.length}</strong>
                </span>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={resetFilters}
                disabled={!hasActiveFilters}
              >
                Reset filters
              </Button>
            </div>
          </div>
        </section>

        {/* Results */}
        <section ref={resultsRef}>
          {paginatedDatasets.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="p-12 text-center space-y-4">
                <BookMascot size="lg" />
                <h3 className="text-xl font-semibold">No datasets found</h3>
                <p className="text-muted-foreground">
                  Try adjusting your search or filters
                </p>
                <Button variant="outline" onClick={resetFilters}>
                  Clear Filters
                </Button>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Dataset Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {paginatedDatasets.map((dataset) => (
                  <DatasetCard
                    key={dataset.id}
                    dataset={dataset}
                    onPreview={() => navigate(`/geojson/${dataset.id}`)}
                    onCreate={() => navigate(`/quiz/studio?dataset=${dataset.id}`)}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <span className="text-sm text-muted-foreground px-4">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </MainLayout>
  );
}

/**
 * Dataset Card Component
 */
interface DatasetCardProps {
  dataset: GeoJsonDataset;
  onPreview: () => void;
  onCreate: () => void;
}

function DatasetCard({ dataset, onPreview, onCreate }: DatasetCardProps) {
  const flag = getCountryFlag(dataset.geography);

  // Determine primary topic icon
  const getTopicIcon = () => {
    const topics = dataset.topics.map(t => t.toLowerCase());
    if (topics.some(t => t.includes('phone') || t.includes('area code'))) {
      return { icon: '📞', label: 'Phone Codes', color: 'bg-blue-500/10 text-blue-700' };
    }
    if (topics.some(t => t.includes('admin') || t.includes('province') || t.includes('state'))) {
      return { icon: '🗺️', label: 'Administrative', color: 'bg-purple-500/10 text-purple-700' };
    }
    if (topics.some(t => t.includes('cit') || t.includes('urban'))) {
      return { icon: '🏙️', label: 'Cities', color: 'bg-orange-500/10 text-orange-700' };
    }
    if (topics.some(t => t.includes('postal') || t.includes('zip'))) {
      return { icon: '📮', label: 'Postal Codes', color: 'bg-green-500/10 text-green-700' };
    }
    return { icon: '📍', label: 'Geographic', color: 'bg-gray-500/10 text-gray-700' };
  };

  const topicInfo = getTopicIcon();

  return (
    <Card className="border border-border/70 bg-card/80 shadow-lg shadow-primary/10 backdrop-blur hover:shadow-xl hover:shadow-primary/15 transition-all group flex flex-col">
      <CardContent className="p-6 flex flex-col flex-1">
        {/* Header with Flag, Title, Size and Topic Badges */}
        <div className="flex items-start gap-3 mb-3">
          <div className="flex-shrink-0 text-3xl leading-none">
            {flag}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors mb-1 line-clamp-2">
              {dataset.title}
            </h3>
            <p className="text-xs text-muted-foreground">{dataset.geography}</p>
          </div>
          <div className="flex items-center gap-1">
            <SizeBadge count={dataset.featureCount} />
            <Badge variant="secondary" className={`shrink-0 text-xs ${topicInfo.color}`}>
              {topicInfo.icon}
            </Badge>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground leading-relaxed mb-4 line-clamp-3">
          {dataset.description}
        </p>

        {/* Properties & Feature Count */}
        <div className="space-y-2 mb-4">
          {/* Properties */}
          <div className="flex items-start gap-2 text-xs">
            <span className="font-semibold text-foreground/70 shrink-0">Properties:</span>
            <span className="text-muted-foreground">
              {dataset.propertyKeys.slice(0, 3).join(', ')}
              {dataset.propertyKeys.length > 3 && ` +${dataset.propertyKeys.length - 3}`}
            </span>
          </div>

          {/* Feature Count */}
          <div className="flex items-center gap-2 text-xs">
            <span className="font-semibold text-foreground/70">Features:</span>
            <span className="text-muted-foreground font-medium">
              {dataset.featureCount.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Actions - Pushed to bottom */}
        <div className="flex gap-2 mt-auto pt-4 border-t border-border/30">
          <Button
            size="sm"
            variant="outline"
            onClick={onPreview}
            className="flex-1 gap-2 rounded-xl"
          >
            <MapPin className="w-4 h-4" />
            Preview
          </Button>
          <Button
            size="sm"
            onClick={onCreate}
            className="flex-1 gap-2 rounded-xl"
          >
            <Sparkles className="w-4 h-4" />
            Create Quiz
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
